import { normalizeResultScope } from './helpers/event_helper';
import { saveFavorites, loadFavorites } from './helpers/local_storage_helper';
import { scrollToAggregate, aggregateIdForMember } from './helpers/scroll_helper';
import { refreshOpenDetails, removeAllDetails } from './details_index';
import { selectHasExpandableScorecards } from '../selectors';
import { nextPollInterval, shouldContinuePolling } from '../utils/poll_utils';
import { showErrorNotification } from '../../shared/helpers';
import { componentText } from '../utils/i18n_utils';
import { VIEW_MODES } from '../constants';

export const ActionTypes = {
  FETCH_RESULT_SCOPE_REQUEST: 'eventV2/FETCH_RESULT_SCOPE_REQUEST',
  FETCH_RESULT_SCOPE_SUCCESS: 'eventV2/FETCH_RESULT_SCOPE_SUCCESS',
  FETCH_RESULT_SCOPE_FAILURE: 'eventV2/FETCH_RESULT_SCOPE_FAILURE',
  REMOVE_RESULT_SCOPE: 'eventV2/REMOVE_RESULT_SCOPE',
  TOGGLE_FAVORITE: 'eventV2/TOGGLE_FAVORITE',
  RETRIEVE_FAVORITES: 'eventV2/RETRIEVE_FAVORITES',
  TOGGLE_FULL_LEADERBOARD: 'eventV2/TOGGLE_FULL_LEADERBOARD',
  CLEAR_EVENT_SLICE: 'eventV2/CLEAR_EVENT_SLICE',
  FULL_CLEAR_EVENT_SLICE: 'eventV2/FULL_CLEAR_EVENT_SLICE',
  SYNC_FAVORITES_FROM_STORAGE: 'eventV2/SYNC_FAVORITES_FROM_STORAGE',
};

const buildEventUrl = (tournamentUrl, roundId, eventId, state) => {
  // Portal templates already carry a query string (source=portal)
  const base = tournamentUrl.replace(':round_id', roundId).replace(':id', eventId);
  if (!selectHasExpandableScorecards(state, eventId)) return base;

  return `${base}${base.includes('?') ? '&' : '?'}expanded_scorecards=true`;
};

// --- AbortController registry (one per in-flight initial fetch) ---
const controllersByEvent = new Map();

// --- Polling state ---
const timersByEvent = new Map(); // eventId -> { timerId, currentInterval }

function clearEventPollingTimer(eventId) {
  const existing = timersByEvent.get(eventId);
  if (!existing) return;
  clearTimeout(existing.timerId);
  timersByEvent.delete(eventId);
}

// Recursive scheduler — adjusts interval based on whether data changed
function schedulePoll(eventId, interval, dispatch, getState) {
  const timerId = setTimeout(async () => {
    // Stop if explicitly cleared externally
    if (!timersByEvent.has(eventId)) return;

    const state = getState();
    if (!state.event?.[eventId]) {
      timersByEvent.delete(eventId);
      return;
    }

    const etag = state.event[eventId].etag;
    const changed = await dispatch(pollResultScope(eventId, etag));

    // Re-check after the async fetch — event may have been closed
    if (!timersByEvent.has(eventId)) return;

    if (changed) dispatch(refreshOpenDetails(eventId));

    // Legacy stop rule (shouldContinuePolling): a not-ongoing round gets this one
    // poll, then the chain ends - unless the customer's polling optimization is
    // off, in which case polling never stops.
    if (!shouldContinuePolling(getState().leaderboardV2)) {
      timersByEvent.delete(eventId);
      return;
    }

    const nextInterval = nextPollInterval(interval, changed, getState().leaderboardV2);
    const nextTimerId = schedulePoll(eventId, nextInterval, dispatch, getState);
    timersByEvent.set(eventId, { timerId: nextTimerId, currentInterval: nextInterval });
  }, interval);

  return timerId;
}

const fetchEventData = async ({ url, etag, signal }) => {
  const headers = {};
  if (etag) headers['If-None-Match'] = etag;

  const res = await fetch(url, { headers, signal });

  if (res.status === 304) {
    return { changed: false };
  }

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const newEtag = res.headers.get('ETag');
  const data = await res.json();

  return {
    changed: true,
    etag: newEtag,
    data: normalizeResultScope(data?.event),
  };
};

// Private: conditional poll using the stored ETag — no loading indicator
// Returns true when the server sent fresh data, false on 304 (Not Modified) or error
const pollResultScope = (eventId, etag) => async (dispatch, getState) => {
  const state = getState();
  const { tournamentUrl, roundId } = state?.leaderboardV2;
  const url = buildEventUrl(tournamentUrl, roundId, eventId, state);

  try {
    const result = await fetchEventData({ url, etag });

    if (!result.changed) return false;

    if (!getState().event?.[eventId]) return false;

    dispatch({
      type: ActionTypes.FETCH_RESULT_SCOPE_SUCCESS,
      payload: {
        eventId: String(eventId),
        data: result.data[String(eventId)],
        etag: result.etag,
      },
    });

    return true;
  } catch {
    return false;
  }
};

export const startEventPolling = (eventId) => (dispatch, getState) => {
  clearEventPollingTimer(eventId);
  const { pollMinInterval } = getState().leaderboardV2;
  const timerId = schedulePoll(eventId, pollMinInterval, dispatch, getState);
  timersByEvent.set(eventId, { timerId, currentInterval: pollMinInterval });
};

// scrollToAggregateId / scrollToMemberId: optional — when the fetch was
// triggered by a member search selection, the row to scroll to once the
// results are rendered. The member id resolves the row in the fetched data
// (the search's aggregate id may belong to another round's board).
// viewMode: the view opening the event, from every caller that has one. The
// board opens in full unless this is one event among several on the web page,
// where saved favorites collapse it to the favorites table behind the Full
// Leaderboard button. A single view (mobile/landscape) and a single-event page
// have nothing else to show; a searched member may not be a favorite, so a
// search never collapses either.
export const fetchResultScope = (eventId, { scrollToAggregateId, scrollToMemberId, viewMode } = {}) => async (dispatch, getState) => {
  const { tournamentUrl, eventsList, roundList, roundId } = getState()?.leaderboardV2;

  let eventIdToUse = '';
  const eventToFetch = Object.values(eventsList).flat().find((e) => e.id === eventId);

  if (eventToFetch && eventToFetch.snapshots.length > 0) {
    const currentRound = roundList.find((r) => r.id === roundId);
    eventIdToUse =
      eventToFetch.snapshots.find((s) => s.index === currentRound?.index)?.id ?? null;
  } else {
    eventIdToUse = eventId;
  }

  clearEventPollingTimer(eventIdToUse);

  controllersByEvent.get(eventIdToUse)?.abort();
  const controller = new AbortController();
  controllersByEvent.set(eventIdToUse, controller);

  dispatch({
    type: ActionTypes.FETCH_RESULT_SCOPE_REQUEST,
    payload: { eventId: eventIdToUse },
  });

  const url = buildEventUrl(tournamentUrl, roundId, eventIdToUse, getState());

  try {
    const result = await fetchEventData({ url, signal: controller.signal });

    if (controller.signal.aborted) return;

    controllersByEvent.delete(eventIdToUse);

    dispatch({
      type: ActionTypes.FETCH_RESULT_SCOPE_SUCCESS,
      payload: {
        eventId: String(eventIdToUse),
        data: result.data[String(eventIdToUse)],
        etag: result.etag,
      },
    });

    const scrollsToRow = Boolean(scrollToAggregateId || scrollToMemberId);
    const favoritesOnly =
      viewMode === VIEW_MODES.WEB && !scrollsToRow && Object.values(eventsList).flat().length > 1;
    dispatch(retrieveFavorites(eventIdToUse, { favoritesOnly }));

    if (scrollsToRow) {
      const eventData = getState().event[eventIdToUse]?.data;
      scrollToAggregate(aggregateIdForMember(eventData, scrollToMemberId) ?? scrollToAggregateId);
    }

    dispatch(startEventPolling(eventIdToUse));
  } catch (err) {
    if (err.name !== 'AbortError') {
      dispatch({
        type: ActionTypes.FETCH_RESULT_SCOPE_FAILURE,
        payload: {
          eventId: eventIdToUse,
          error: err.message || componentText('shared.request_failed', 'Request failed'),
        },
      });
    }

    controllersByEvent.delete(eventIdToUse);
  }
};

export const removeResultScope = (eventId) => (dispatch) => {
  controllersByEvent.get(eventId)?.abort();
  controllersByEvent.delete(eventId);
  clearEventPollingTimer(eventId);

  dispatch(removeAllDetails(eventId));

  dispatch({
    type: ActionTypes.REMOVE_RESULT_SCOPE,
    payload: { eventId },
  });
};

// deferListMirror: skips the favoritesByEvent update (the events-list split)
// while still persisting and updating the event slice - used by the
// optimistic add so "My Leaderboards"/"Leaderboards" regroup once, when the
// played-events fan-out settles, instead of flickering through the
// one-event intermediate state. syncFavoritesFromStorage catches the
// mirror up afterwards.
export const toggleFavorite = (eventId, memberId, { deferListMirror = false } = {}) => (dispatch, getState) => {
  const state = getState();

  const parentId = getParentId(state, eventId);

  const prevFavorites = state.event?.[eventId]?.favorites ?? [];

  const memberIdStr = String(memberId);
  const nextFavorites = prevFavorites.includes(memberIdStr)
    ? prevFavorites.filter((id) => id !== memberIdStr)
    : [...prevFavorites, memberIdStr];

  // No optimistic toggle: only reflect the change once it is persisted, so
  // the star never shows a state that would be lost on reload.
  if (!saveFavorites(parentId, nextFavorites)) {
    showErrorNotification(favoritesSaveFailedMessage());
    return;
  }

  dispatch({
    type: ActionTypes.TOGGLE_FAVORITE,
    // parentId is included so leaderboardV2_reducer can sync favoritesByEvent
    payload: { eventId, parentId, favorites: nextFavorites, deferListMirror },
  });
};

const favoritesSaveFailedMessage = () =>
  componentText('aggregate_row.favorites_save_failed', 'Your favorites could not be saved. Please refresh the page and try again.');

// favoritesOnly: collapse the board to the favorites table (see fetchResultScope).
export const retrieveFavorites = (eventId, { favoritesOnly = false } = {}) => (dispatch, getState) => {
  const cachedFavorites = loadFavorites();
  if (Object.keys(cachedFavorites).length === 0) return;

  const state = getState();
  const parentId = getParentId(state, eventId);

  if (!cachedFavorites[parentId]) return;

  dispatch({
    type: ActionTypes.RETRIEVE_FAVORITES,
    payload: { eventId, cachedFavorites: cachedFavorites[parentId], favoritesOnly },
  });
};

const getParentId = (state, eventId) => {
  const allEvents = Object.values(state.leaderboardV2.eventsList).flat();
  const parentId = allEvents
    .find((ev) => ev.snapshots?.some((s) => s.id === eventId))
    ?.id;
  return parentId || eventId;
};

export const toggleFullLeaderboard = (eventId) => ({
  type: ActionTypes.TOGGLE_FULL_LEADERBOARD,
  payload: { eventId },
});

// Open scorecards go with their event: they are keyed by the players, so a
// left-over entry would reopen with stale data the next time the same event
// (e.g. a multi-round tournament on another round) renders those players.
export const clearEventSlice = (eventIdToKeep) => (dispatch, getState) => {
  Object.keys(getState().event).forEach((id) => {
    if (id !== String(eventIdToKeep)) {
      controllersByEvent.get(id)?.abort();
      controllersByEvent.delete(id);
      clearEventPollingTimer(id);
      dispatch(removeAllDetails(id));
    }
  });

  dispatch({
    type: ActionTypes.CLEAR_EVENT_SLICE,
    payload: { eventIdToKeep },
  });
};

export const fullClearEventSlice = () => (dispatch, getState) => {
  // Abort in-flight fetches too — a late FETCH_RESULT_SCOPE_SUCCESS would
  // repopulate the slice right after the clear.
  controllersByEvent.forEach((controller) => controller.abort());
  controllersByEvent.clear();
  timersByEvent.forEach((_, eventId) => clearEventPollingTimer(eventId));
  Object.keys(getState().details ?? {}).forEach((eventId) => dispatch(removeAllDetails(eventId)));

  dispatch({ type: ActionTypes.FULL_CLEAR_EVENT_SLICE });
};

export const syncFavoritesFromStorage = () => (dispatch) => {
  dispatch({ type: ActionTypes.SYNC_FAVORITES_FROM_STORAGE, payload: loadFavorites() });
};
